import { Box } from '@mui/material';
import { debounce } from '@mui/material/utils';
import {
  GridToolbarContainer,
  gridQuickFilterValuesSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import SearchField from 'components/controls/SearchField';
import { node, number, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const defaultSearchValueParser = searchText =>
  searchText.match(/(?:\S+:"[^"]*"|\S+:\S+|\S+)/g) || [];

const AgenticPanelToolbar = ({
  debounceMs = 300,
  searchPlaceholder = I18n.t('AgenticChatFlow.Search'),
  children = null,
}) => {
  const apiRef = useGridApiContext();
  const quickFilterValues = useGridSelector(apiRef, gridQuickFilterValuesSelector) ?? [];
  const gridSearchValue = quickFilterValues.join(' ');
  const [value, setValue] = useState(gridSearchValue);

  useEffect(() => {
    setValue(gridSearchValue);
  }, [gridSearchValue]);

  const updateSearchValue = useCallback(
    newSearchValue => {
      apiRef.current.setQuickFilterValues(defaultSearchValueParser(newSearchValue));
    },
    [apiRef],
  );

  const debouncedUpdateSearchValue = useMemo(
    () => debounce(updateSearchValue, debounceMs),
    [updateSearchValue, debounceMs],
  );

  const handleSearchValueChange = useCallback(
    event => {
      const newSearchValue = event.target.value;
      debouncedUpdateSearchValue(newSearchValue);
      setValue(newSearchValue);
    },
    [debouncedUpdateSearchValue],
  );

  return (
    <GridToolbarContainer sx={{ p: 0, my: 2, gap: 1, alignItems: 'center' }}>
      <Box sx={{ width: { xs: '100%', sm: 280, md: 320 } }}>
        <SearchField
          value={value}
          onChange={handleSearchValueChange}
          placeholder={searchPlaceholder}
        />
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {children}
    </GridToolbarContainer>
  );
};

AgenticPanelToolbar.propTypes = {
  debounceMs: number,
  searchPlaceholder: string,
  children: node,
};

export default AgenticPanelToolbar;
